import React, { useState, useEffect, useCallback } from "react";
import {
  Eye, Users, FileText, Target, TrendingUp, TrendingDown,
  AlertTriangle, CheckCircle2, Minus, ChevronDown, ChevronUp,
  School, BarChart3
} from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const WIDA_COLORS = {
  1: { bg: "#fef2f2", bar: "#ef4444", label: "Entering" },
  2: { bg: "#fff7ed", bar: "#f97316", label: "Emerging" },
  3: { bg: "#fefce8", bar: "#eab308", label: "Developing" },
  4: { bg: "#f0fdf4", bar: "#22c55e", label: "Expanding" },
  5: { bg: "#eff6ff", bar: "#3b82f6", label: "Bridging" },
  6: { bg: "#faf5ff", bar: "#8b5cf6", label: "Reaching" },
};

const MODALITY_COLORS = {
  listening: "#3b82f6",
  speaking: "#8b5cf6",
  reading: "#22c55e",
  writing: "#f59e0b",
};

const TRAJECTORY_CONFIG = {
  on_track: { label: "On Track", color: "#22c55e", bg: "#f0fdf4", icon: CheckCircle2 },
  at_risk: { label: "At Risk", color: "#f59e0b", bg: "#fffbeb", icon: AlertTriangle },
  needs_intervention: { label: "Needs Intervention", color: "#ef4444", bg: "#fef2f2", icon: AlertTriangle },
  growing: { label: "Growing", color: "#3b82f6", bg: "#eff6ff", icon: TrendingUp },
  stable: { label: "Stable", color: "#6b7280", bg: "#f9fafb", icon: Minus },
  declining: { label: "Declining", color: "#dc2626", bg: "#fef2f2", icon: TrendingDown },
};

const DemoAnalyticsPreview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchPreview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/demo-preview`);
      setData(res.data);
      setExpanded(true);
    } catch (err) {
      console.error("Preview fetch failed:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  if (!expanded && !data) {
    return (
      <div className="border-2 border-dashed border-gray-300 p-6 text-center" data-testid="demo-preview-collapsed">
        <Eye size={24} className="mx-auto mb-2 text-gray-400" />
        <p className="text-sm text-gray-600 font-medium mb-3">Preview what the demo teacher dashboard will look like</p>
        <Button
          onClick={fetchPreview}
          disabled={loading}
          variant="outline"
          className="btn-sharp"
          data-testid="load-preview-btn"
        >
          {loading ? "Loading Preview..." : "Load Analytics Preview"}
        </Button>
      </div>
    );
  }

  if (loading || !data) {
    return <div className="text-center py-8 text-gray-500 text-sm">Loading preview data...</div>;
  }

  const { overview, trajectories, school_performance, teacher_summaries } = data;
  const maxLevelCount = Math.max(...Object.values(overview.level_distribution));

  return (
    <div className="space-y-5" data-testid="demo-analytics-preview">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Eye size={18} className="text-[#002FA7]" />
          <h3 className="text-base font-bold">Demo Analytics Preview</h3>
          <span className="text-[9px] font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded">WHAT REPS WILL SEE</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={fetchPreview} className="btn-sharp text-xs" data-testid="refresh-preview-btn">
            Refresh
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)} className="btn-sharp">
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </Button>
        </div>
      </div>

      {expanded && (
        <>
          {/* Key Metrics Row */}
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-white border border-gray-200 p-4 text-center">
              <div className="text-3xl font-black text-[#002FA7]">{overview.total_students}</div>
              <div className="text-[10px] text-gray-500 font-medium uppercase tracking-wider mt-1">Students</div>
            </div>
            <div className="bg-white border border-gray-200 p-4 text-center">
              <div className="text-3xl font-black text-[#002FA7]">{overview.total_results}</div>
              <div className="text-[10px] text-gray-500 font-medium uppercase tracking-wider mt-1">Assessments</div>
            </div>
            <div className="bg-white border border-gray-200 p-4 text-center">
              <div className="text-3xl font-black text-[#002FA7]">{overview.total_goals}</div>
              <div className="text-[10px] text-gray-500 font-medium uppercase tracking-wider mt-1">Goals Set</div>
            </div>
            <div className="bg-white border border-gray-200 p-4 text-center">
              <div className="text-3xl font-black text-[#002FA7]">{overview.avg_composite}</div>
              <div className="text-[10px] text-gray-500 font-medium uppercase tracking-wider mt-1">Avg Composite</div>
            </div>
          </div>

          {/* Modality Averages + Level Distribution */}
          <div className="grid grid-cols-2 gap-4">
            {/* Modality Averages */}
            <div className="bg-white border border-gray-200 p-4">
              <div className="text-xs font-bold text-gray-700 mb-3 uppercase tracking-wider">Avg Proficiency Level by Modality</div>
              <div className="space-y-3">
                {["listening", "speaking", "reading", "writing"].map(mod => (
                  <div key={mod} className="flex items-center gap-3">
                    <div className="w-16 text-xs font-semibold capitalize text-gray-600">{mod}</div>
                    <div className="flex-1 bg-gray-100 h-6 relative overflow-hidden">
                      <div
                        className="h-full transition-all duration-700"
                        style={{
                          width: `${(overview.modality_avgs[mod] / 6) * 100}%`,
                          backgroundColor: MODALITY_COLORS[mod],
                        }}
                      />
                    </div>
                    <div className="w-10 text-right text-sm font-bold" style={{ color: MODALITY_COLORS[mod] }}>
                      {overview.modality_avgs[mod]}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Level Distribution */}
            <div className="bg-white border border-gray-200 p-4">
              <div className="text-xs font-bold text-gray-700 mb-3 uppercase tracking-wider">Proficiency Level Distribution</div>
              <div className="flex items-end gap-2 h-28">
                {Object.entries(overview.level_distribution).map(([level, count]) => {
                  const config = WIDA_COLORS[parseInt(level)];
                  const height = maxLevelCount > 0 ? (count / maxLevelCount) * 100 : 0;
                  return (
                    <div key={level} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-[10px] font-bold text-gray-600">{count}</span>
                      <div className="w-full relative" style={{ height: `${Math.max(height, 4)}%`, minHeight: "4px" }}>
                        <div className="absolute inset-0" style={{ backgroundColor: config.bar, opacity: 0.8 }} />
                      </div>
                      <span className="text-[9px] font-semibold text-gray-500">L{level}</span>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between text-[8px] text-gray-400 mt-1 px-1">
                <span>Entering</span>
                <span>Reaching</span>
              </div>
            </div>
          </div>

          {/* Trajectory Summary */}
          <div className="bg-white border border-gray-200 p-4">
            <div className="text-xs font-bold text-gray-700 mb-3 uppercase tracking-wider">Student Trajectory Snapshot</div>
            <div className="grid grid-cols-6 gap-2">
              {Object.entries(TRAJECTORY_CONFIG).map(([key, config]) => {
                const Icon = config.icon;
                const count = trajectories[key] || 0;
                return (
                  <div key={key} className="text-center p-3" style={{ backgroundColor: config.bg }} data-testid={`trajectory-${key}`}>
                    <Icon size={16} className="mx-auto mb-1" style={{ color: config.color }} />
                    <div className="text-xl font-black" style={{ color: config.color }}>{count}</div>
                    <div className="text-[9px] font-semibold text-gray-500 mt-0.5">{config.label}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* School Performance */}
          <div className="bg-white border border-gray-200 p-4">
            <div className="text-xs font-bold text-gray-700 mb-3 uppercase tracking-wider flex items-center gap-2">
              <School size={14} /> School Performance Comparison
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-[#002FA7] text-white">
                    <th className="p-2 text-left font-bold">School</th>
                    <th className="p-2 text-center font-bold">Students</th>
                    <th className="p-2 text-center font-bold">Results</th>
                    <th className="p-2 text-center font-bold">Composite</th>
                    <th className="p-2 text-center font-bold">L</th>
                    <th className="p-2 text-center font-bold">S</th>
                    <th className="p-2 text-center font-bold">R</th>
                    <th className="p-2 text-center font-bold">W</th>
                  </tr>
                </thead>
                <tbody>
                  {school_performance.map((s, i) => (
                    <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                      <td className="p-2 font-semibold">{s.school_name}</td>
                      <td className="p-2 text-center">{s.student_count}</td>
                      <td className="p-2 text-center">{s.result_count}</td>
                      <td className="p-2 text-center font-bold" style={{ color: s.avg_composite >= 4 ? "#22c55e" : s.avg_composite >= 3 ? "#f59e0b" : "#ef4444" }}>
                        {s.avg_composite}
                      </td>
                      {["listening", "speaking", "reading", "writing"].map(mod => (
                        <td key={mod} className="p-2 text-center" style={{ color: MODALITY_COLORS[mod] }}>
                          {s.modality_avgs[mod]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Teacher Summary */}
          <div className="bg-white border border-gray-200 p-4">
            <div className="text-xs font-bold text-gray-700 mb-3 uppercase tracking-wider flex items-center gap-2">
              <BarChart3 size={14} /> Teacher Dashboard Preview
            </div>
            <div className="grid grid-cols-2 gap-3">
              {teacher_summaries.map((t, i) => (
                <div key={i} className="border border-gray-200 p-3 bg-slate-50">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-bold text-sm">{t.name}</div>
                      <div className="text-[10px] text-gray-500">{t.school_name}</div>
                    </div>
                    <span className={`text-[9px] font-bold px-2 py-0.5 rounded ${
                      t.avg_composite >= 4 ? "bg-green-100 text-green-700" :
                      t.avg_composite >= 3.5 ? "bg-blue-100 text-blue-700" :
                      t.avg_composite >= 3 ? "bg-amber-100 text-amber-700" :
                      "bg-red-100 text-red-700"
                    }`}>Avg {t.avg_composite}</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div>
                      <div className="text-lg font-bold">{t.class_count}</div>
                      <div className="text-[9px] text-gray-400">Classes</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold">{t.student_count}</div>
                      <div className="text-[9px] text-gray-400">Students</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold">{t.result_count}</div>
                      <div className="text-[9px] text-gray-400">Results</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold">{t.goals_set}</div>
                      <div className="text-[9px] text-gray-400">Goals</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Verdict */}
          <div className={`p-4 border ${
            overview.total_results > 100 && overview.total_goals > 10
              ? "bg-green-50 border-green-200"
              : "bg-amber-50 border-amber-200"
          }`}>
            <div className="flex items-center gap-2">
              {overview.total_results > 100 && overview.total_goals > 10 ? (
                <>
                  <CheckCircle2 size={18} className="text-green-600" />
                  <div>
                    <span className="text-sm font-bold text-green-800">Demo Ready</span>
                    <span className="text-xs text-green-700 ml-2">
                      {overview.total_students} students, {overview.total_results} results, {overview.total_goals} goals across {school_performance.length} schools. Data is compelling for a demo.
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <AlertTriangle size={18} className="text-amber-600" />
                  <div>
                    <span className="text-sm font-bold text-amber-800">Needs More Data</span>
                    <span className="text-xs text-amber-700 ml-2">
                      Consider seeding demo data to populate more results and goals for a compelling demo experience.
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DemoAnalyticsPreview;
